import React from "react";
import { useMutation, useQueryClient } from "react-query";
import { toast } from "react-toastify";
import useAllPlayers from "../../hooks/queryResults/useAllPlayers";
import useIsLoading from "../../hooks/queryResults/useIsLoading";
import useWaiversRequests from "../../hooks/queryResults/useWaiversRequests";
import useUrls from "../../hooks/useUrls";
import http from "../../services/httpService";
import Util from "../../util/util";
import Box from "../layout/Box";
import PlayerImageByPos from "../player/PlayerImageByPos";
import { getPlayerName } from "../player/PlayerName";
import Loading from "../ui/Loading";
import {
  SortableContainer,
  SortableElement,
  SortableHandle,
} from "react-sortable-hoc";
import { arrayMoveImmutable } from "array-move";

export default function TeamWaiversRequests({ team }) {
  const { waiversRequestsData, waiversRequestsQuery, waiversRequestsUrl } =
    useWaiversRequests(team?.obj.id);
  const { playersData, playersQuery } = useAllPlayers();
  const { apiUrl } = useUrls();
  const queryClient = useQueryClient();

  const cancelRequestMutation = useMutation(
    (requestId) => {
      const url = `${apiUrl}/rosters/requests/${requestId}`;
      const config = Util.addTokenToHeaders();
      console.log(`sending DELETE request to url ${url} with no data`);
      return http.delete(url, config);
    },
    {
      onSuccess: async () => {
        console.log("add request mutation - success");
        await queryClient.refetchQueries(waiversRequestsUrl);
        toast.success("Successfully cancelled waivers request");
      },
      onError: (error) => {
        toast.error(error.message);
        // todo: send sentry error
        console.error(error);
      },
    }
  );

  const reorderRequestsMutation = useMutation(
    (requestIds) => {
      const data = { requests: requestIds };
      const url = `${apiUrl}/rosters/requests/${team.obj.id}/reorder`;
      const config = Util.addTokenToHeaders();
      console.log(`sending PATCH request to url ${url} with data: ${data}`);
      return http.patch(url, data, config);
    },
    {
      onSuccess: async () => {
        console.log("reorder requests mutation - success");
        await queryClient.refetchQueries(waiversRequestsUrl);
        toast.success("Successfully reordered waivers requests");
      },
      onError: (error) => {
        toast.error(error.message);
        // todo: send sentry error
        console.error(error);
      },
    }
  );

  const isLoading = useIsLoading(
    team,
    waiversRequestsData,
    waiversRequestsQuery,
    playersData,
    playersQuery
  );
  if (isLoading) return <Loading />;

  const requests = waiversRequestsData.requests.map((request) => request.id);

  const DragHandle = SortableHandle(() => (
    <td style={{ cursor: "row-resize", paddingRight: "0.5rem" }}>:::</td>
  ));

  const SortableItem = SortableElement(({ value: requestId }) => {
    console.log("requestId", requestId);

    const request = waiversRequestsData.requests.filter(
      (request) => request.id === requestId
    )[0];

    const { num } = request;

    const printPlayer = (playerId) => {
      const player = playersData.players[playerId];
      return (
        <div key={playerId} style={{ display: "flex", alignItems: "center" }}>
          <PlayerImageByPos
            player={player}
            style={{
              maxWidth: "18px",
            }}
          />
          <div style={{ marginLeft: "0.25rem" }}>
            {getPlayerName(player, { pos: 1, nfl: 1, injury: 1 })}
          </div>
        </div>
      );
    };

    let playerIn = null;
    if (request.playerIn) {
      playerIn = printPlayer(request.playerIn);
    }

    let playerOut = null;
    if (request.playerOut) {
      playerOut = printPlayer(request.playerOut);
    }

    return (
      <tr key={requestId}>
        <DragHandle />
        <td>{requestId}</td>
        <td>{num}</td>
        <td>{playerIn}</td>
        <td>{playerOut}</td>
        <td>
          <button
            className={"btn"}
            onClick={() => cancelRequestMutation.mutate(request.id)}
          >
            Cancel
          </button>
        </td>
      </tr>
    );
  });

  const SortableList = SortableContainer(({ items }) => {
    return (
      <table>
        <thead>
          <tr>
            <th>Reorder</th>
            <th>Id</th>
            <th>Num</th>
            <th>Add</th>
            <th>Drop</th>
            <th>Action</th>
          </tr>
        </thead>
        <tbody>
          {items.map((value, i) => (
            <SortableItem key={`item-${value}`} index={i} value={value} />
          ))}
        </tbody>
      </table>
    );
  });

  const handleSortEnd = ({ oldIndex, newIndex }) => {
    const newRequests = arrayMoveImmutable(requests, oldIndex, newIndex);
    console.log("newRequests", newRequests);
    reorderRequestsMutation.mutate(newRequests);
  };

  return (
    <Box title={"waivers requests"} centerContent={true}>
      {!waiversRequestsData.requests.length ? (
        <p>No waivers requests found.</p>
      ) : (
        <SortableList
          items={requests}
          onSortEnd={handleSortEnd}
          useDragHandle
        />
      )}
    </Box>
  );
}
